import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const scopes = [
  {
    icon: "ri-film-line",
    title: "电影内容",
    desc: "覆盖科幻、悬疑、爱情、历史、动作等多种类型，梳理值得一看的优质影片。",
  },
  {
    icon: "ri-tv-2-line",
    title: "电视剧内容",
    desc: "聚合都市、刑侦、奇幻、年代等热门剧集，帮助你快速找到想追的剧。",
  },
  {
    icon: "ri-mic-line",
    title: "综艺内容",
    desc: "涵盖竞技、美食、音乐、推理等综艺类型，轻松解压又充满看点。",
  },
];

const promises = [
  { icon: "ri-shield-check-line", text: "严谨整理内容信息，力求准确、清晰、可追溯。" },
  { icon: "ri-user-heart-line", text: "以用户浏览体验为先，路径清晰、操作简单。" },
  { icon: "ri-copyright-line", text: "尊重内容版权，仅提供导览与信息聚合，不提供播放与下载。" },
  { icon: "ri-refresh-line", text: "持续更新影视资源与资讯动态，保持内容鲜活。" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - CC视频",
    description:
      "了解CC视频：一个聚焦精选影视资源与影视资讯的内容平台，涵盖电影、电视剧与综艺，致力于提供清晰友好的内容浏览与发现体验。",
    keywords: "关于CC视频,影视内容平台,影视资讯,电影,电视剧,综艺",
  });

  return (
    <div className="pb-16 md:pb-20">
      <div className="bg-primary-900">
        <div className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="[&_a]:text-background-300 [&_span]:text-background-50 [&_i]:text-background-500">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          </div>
          <h1 className="mt-4 text-3xl md:text-4xl font-heading font-bold text-background-50">
            关于 CC视频
          </h1>
          <p className="mt-3 text-sm md:text-base text-background-300 max-w-2xl leading-relaxed">
            精选影视视频内容平台 —— 让每一次寻找，都能更快遇见想看的作品。
          </p>
        </div>
      </div>

      <section className="max-w-content mx-auto px-4 md:px-6 mt-10 md:mt-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-heading font-bold text-foreground-950">
              平台定位
            </h2>
            <div className="mt-4 space-y-3 text-sm md:text-base text-foreground-700 leading-loose">
              <p>
                CC视频是一个聚焦精选影视内容与影视资讯的门户平台。我们围绕电影、电视剧、综艺三大频道，为用户提供内容浏览、分类筛选、关键词搜索与详情查看服务。
              </p>
              <p>
                我们希望做的不只是罗列作品，而是通过清晰的信息结构与友好的浏览路径，帮助影视爱好者快速找到感兴趣的内容，并顺藤摸瓜发现更多相关佳作。
              </p>
              <p>
                本站提供内容导览与信息聚合服务，不提供在线播放、下载、会员或支付功能，力求让浏览回归纯粹。
              </p>
            </div>
          </div>
          <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimal%20film%20editing%20studio%20with%20a%20large%20screen%20and%20film%20reels%20on%20a%20desk%2C%20warm%20amber%20accent%20lighting%20with%20cool%20blue%20shadows%2C%20clean%20editorial%20photography%20of%20media%20production&width=1200&height=800&seq=ccvideo-about-1&orientation=landscape"
              alt="CC视频内容运营场景"
              title="CC视频 - 精选影视视频内容平台"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-6 mt-12 md:mt-16">
        <h2 className="text-2xl font-heading font-bold text-foreground-950">
          内容范围
        </h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {scopes.map((item) => (
            <div
              key={item.title}
              className="p-6 rounded-lg bg-background-100 border border-background-200"
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-6 mt-12 md:mt-16">
        <div className="rounded-lg bg-background-100 p-6 md:p-10">
          <h2 className="text-2xl font-heading font-bold text-foreground-950">
            服务承诺
          </h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {promises.map((p) => (
              <div key={p.text} className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${p.icon} text-lg`}></i>
                </span>
                <p className="text-sm text-foreground-700 leading-relaxed pt-1.5">
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-6 mt-12 md:mt-16">
        <h2 className="text-2xl font-heading font-bold text-foreground-950">
          团队介绍
        </h2>
        <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose max-w-3xl">
          CC视频由一支热爱影视内容的小团队运营，成员包括内容编辑、产品设计与技术开发，负责内容整理、信息架构与平台维护。我们相信，好的内容值得被更好地被发现。
        </p>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-6 mt-12 md:mt-16">
        <div className="rounded-lg bg-primary-900 p-6 md:p-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-background-50">
              想了解更多或有合作意向？
            </h2>
            <p className="mt-2 text-sm text-background-300">
              欢迎通过联系页面与我们取得联系，我们会尽快回复你。
            </p>
          </div>
          <Link
            to="/contact"
            className="h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-send-line"></i>
            联系我们
          </Link>
        </div>
      </section>
    </div>
  );
}